import { type Theme, SvgIconProps } from '@mui/material';
import GestureOutlinedIcon from '@mui/icons-material/GestureOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';

import { WorkflowStepKind, DoclogixApprovalRecipients } from 'src/@types/workflow/types';

export type StepPaletteColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export interface StepKind {
  labelKey: string;
  emailLabelKey?: string;
  hintKey?: string;
  icon: React.ComponentType<SvgIconProps>;
  color: StepPaletteColor;
}

export const STEP_KINDS: Record<WorkflowStepKind, StepKind> = {
  signatureQualified: {
    labelKey: 'workflows.stepKinds.signatureQualified',
    emailLabelKey: 'workflows.fields.signer',
    icon: DriveFileRenameOutlineIcon,
    color: 'secondary',
  },
  signatureUnqualified: {
    labelKey: 'workflows.stepKinds.signatureUnqualified',
    emailLabelKey: 'workflows.fields.signer',
    icon: GestureOutlinedIcon,
    color: 'info',
  },
  acknowledgement: {
    labelKey: 'workflows.stepKinds.acknowledgement',
    emailLabelKey: 'workflows.fields.assignee',
    icon: CheckCircleOutlinedIcon,
    color: 'success',
  },
  endorsement: {
    labelKey: 'workflows.stepKinds.endorse',
    emailLabelKey: 'workflows.fields.assignee',
    icon: HistoryEduOutlinedIcon,
    color: 'error',
  },
  notification: {
    labelKey: 'workflows.stepKinds.notification',
    emailLabelKey: 'workflows.fields.recipient',
    icon: ForwardToInboxOutlinedIcon,
    color: 'warning',
  },
  doclogixApproval: {
    labelKey: 'workflows.stepKinds.doclogixApproval',
    hintKey: 'workflows.doclogixApprovalStepHint',
    icon: CloudUploadOutlinedIcon,
    color: 'primary',
  },
  doclogixUpdate: {
    labelKey: 'workflows.stepKinds.doclogixUpdate',
    hintKey: 'workflows.doclogixUpdateStepHint',
    icon: CloudSyncOutlinedIcon,
    color: 'primary',
  },
};

export const DOCLOGIX_RECIPIENT_LABEL_KEYS: Record<DoclogixApprovalRecipients, string> = {
  none: 'workflows.fields.doclogixRecipientsNone',
  ownerManager: 'workflows.fields.doclogixRecipientsOwnerManager',
};

export const stepColorPath = (kind: WorkflowStepKind) => `${STEP_KINDS[kind].color}.main`;

export const stepColor = (theme: Theme, kind: WorkflowStepKind) =>
  theme.palette[STEP_KINDS[kind].color].main;

type StepKindIconProps = Omit<SvgIconProps, 'kind'> & { kind: WorkflowStepKind };

export function StepKindIcon({ kind, ...rest }: StepKindIconProps) {
  const Icon = STEP_KINDS[kind].icon;

  return <Icon {...rest} />;
}
